import type { LocalDate, LocalTime } from './datetime';
import { daysBetween, dayOfWeek } from './datetime';

const JUNK = new Set(['null', 'undefined', 'n/a', 'na', '-', '--', 'none', 'nil']);

const WEEKDAYS = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
const WEEKDAYS_SHORT = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];
const MONTHS_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** Utilidades de presentación puras, comprobables sin renderizar nada. */
export const Formatters = {
  /** "$", "$$", "$$$", "$$$$". Devuelve `null` para que la UI **oculte** el componente. */
  priceLevel(level: number | null | undefined): string | null {
    if (level == null || level < 1 || level > 4 || !Number.isInteger(level)) return null;
    return '$'.repeat(level);
  },

  /** "350 m" / "1.2 km". `null` ⇒ el chip de distancia no se renderiza. */
  distance(meters: number | null | undefined): string | null {
    if (meters == null || meters < 0) return null;
    if (meters < 1000) return `${meters} m`;
    return `${(meters / 1000).toFixed(1)} km`;
  },

  /** "4.6". `null` ⇒ no se dibuja el bloque de calificación. */
  rating(value: number | null | undefined, reviewCount: number): string | null {
    if (value == null || reviewCount <= 0) return null;
    return value.toFixed(1);
  },

  /**
   * Descarta textos que no aportan nada. Muchos backends envían literalmente "null",
   * "undefined" o "N/A"; ninguno de ellos debe llegar a la pantalla.
   */
  sanitize(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    if (!trimmed) return null;
    return JUNK.has(trimmed.toLowerCase()) ? null : trimmed;
  },

  /** "Hoy" / "Mañana" / "vie, 12 sep". */
  friendlyDate(date: LocalDate, today: LocalDate): string {
    const diff = daysBetween(today, date);
    if (diff === 0) return 'Hoy';
    if (diff === 1) return 'Mañana';
    const [, month, day] = date.split('-').map(Number);
    return `${WEEKDAYS_SHORT[dayOfWeek(date) - 1]}, ${day} ${MONTHS_SHORT[month - 1]}`;
  },

  /** "viernes 12 de septiembre". Formato largo para la confirmación y el detalle. */
  longDate(date: LocalDate): string {
    const months = [
      'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
      'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
    ];
    const [, month, day] = date.split('-').map(Number);
    return `${WEEKDAYS[dayOfWeek(date) - 1]} ${day} de ${months[month - 1]}`;
  },

  weekdayName(day: number): string {
    return WEEKDAYS[day - 1] ?? '';
  },

  /** "20:30". La hora ya viene en formato de reloj, solo se recorta por seguridad. */
  time(time: LocalTime): string {
    return time.slice(0, 5);
  },

  partySize(size: number): string {
    return size === 1 ? '1 persona' : `${size} personas`;
  },
};
